import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { DonationSummary } from '@foundation/shared';
import { DONATION_STATUS_LABEL_TH } from '@foundation/shared';
import { api, openReceipt } from '../lib/api';
import { formatDate, formatSatang } from '../lib/format';

interface HistoryResponse {
  data: DonationSummary[];
  summary: { totalAmount: number; donationCount: number; pendingCount: number };
}

const STATUS_TONE: Record<string, string> = {
  verified: 'green',
  pending_verify: 'amber',
  rejected: 'red',
};

export default function History() {
  const navigate = useNavigate();
  const [res, setRes] = useState<HistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    api<HistoryResponse>('/donations/me', { signal: ac.signal })
      .then(setRes)
      .catch((e: unknown) => {
        if (!ac.signal.aborted) setError(e instanceof Error ? e.message : 'โหลดประวัติไม่สำเร็จ');
      });
    return () => ac.abort();
  }, []);

  return (
    <div className="screen">
      <div className="topbar">
        <button className="back" onClick={() => navigate('/')}>← หน้าแรก</button>
        <h1>ประวัติการบริจาค</h1>
      </div>

      {error && <div className="alert error">{error}</div>}
      {receiptError && <div className="alert error">{receiptError}</div>}

      {res && (
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="muted small">ยอดบริจาคสะสมของท่าน</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--c-primary-dark)' }}>
            {formatSatang(res.summary.totalAmount)}
          </div>
          <div className="muted small">
            จาก {res.summary.donationCount} ครั้ง
            {res.summary.pendingCount > 0 && ` · รอตรวจสอบอีก ${res.summary.pendingCount} รายการ`}
          </div>
          <div style={{ marginTop: 10 }} className="small">
            ขออนุโมทนาบุญกับท่าน 🙏
          </div>
        </div>
      )}

      {res === null ? (
        <div className="center">กำลังโหลด…</div>
      ) : res.data.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>ยังไม่มีประวัติการบริจาค</p>
        </div>
      ) : (
        res.data.map((d) => (
          <div className="list-item" key={d.id}>
            <div className="row">
              <div style={{ minWidth: 0 }}>
                <div className="list-title">{formatSatang(d.amount)}</div>
                <div className="list-sub">
                  {formatDate(d.donatedAt)}
                  {d.campaignName && ` · ${d.campaignName}`}
                </div>
                <div className="list-sub mono">{d.donationNo}</div>
                {d.receiptNo && (
                  <div className="list-sub">
                    ใบอนุโมทนาบัตร <span className="mono">{d.receiptNo}</span>
                    {d.receiptId && (
                      <>
                        {' · '}
                        <button
                          className="link"
                          onClick={() => {
                            setReceiptError(null);
                            openReceipt(d.receiptId!).catch(() =>
                              setReceiptError('เปิดใบอนุโมทนาบัตรไม่สำเร็จ กรุณาลองใหม่'),
                            );
                          }}
                        >
                          เปิด / บันทึก
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              <span className={`badge ${STATUS_TONE[d.status] ?? ''}`}>
                {DONATION_STATUS_LABEL_TH[d.status] ?? d.status}
              </span>
            </div>
          </div>
        ))
      )}

      <Link className="btn block" to="/" style={{ marginTop: 8 }}>
        ❤️ บริจาคอีกครั้ง
      </Link>
    </div>
  );
}

export function DonationDone() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const no = params.get('no');

  return (
    <div className="splash">
      <div style={{ fontSize: 44 }}>🙏</div>
      <h1 style={{ fontSize: 20, margin: 0 }}>ขออนุโมทนาบุญ</h1>
      <p className="muted">
        ได้รับข้อมูลแจ้งโอนแล้ว
        {no && (
          <>
            <br />
            เลขที่ <span className="mono">{no}</span>
          </>
        )}
        <br />
        เจ้าหน้าที่จะตรวจสอบภายใน 1 วันทำการ
        <br />
        และแจ้งผลกลับทาง LINE นี้
      </p>
      <button className="block" onClick={() => navigate('/history')}>
        ดูประวัติการบริจาค
      </button>
    </div>
  );
}
